/**
 * "Preview" gadget (Build): a live thumbnail of the project's main service (the share link's default, utils/shareEntry
 * defaultEntry: `app`, else the first), drawn in a sandboxed iframe scaled down (same origin, as every other preview:
 * without it the app gets a null origin and its scripts and API calls through /api/preview fail; no
 * click goes through), with Open, and Start / Stop / Restart / Logs (sandbox/ServiceActions) for someone who may run
 * it. The thumbnail fills the card's height (a tall card shows more of the page): utils/homeGadgets/previewFit, re-measured
 * with a ResizeObserver when the card, the window or the grid row changes size (a tall gadget has a height of its own:
 * HomeGadgets.css). Not running: says so, with Start, which starts the whole project (`runAll`: the Home's Start all, so the app's
 * other services and its compose stack come up with it). A viewer sees the thumbnail and Open, never the run controls.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { serviceState } from '../../../services/SandboxService';
import { shortServiceName } from '../../../utils/gitRepoSource';
import { mainServiceItem } from '../../../utils/homeGadgets/build';
import { previewFit } from '../../../utils/homeGadgets/previewFit';
import { appUrlOf, openApp } from '../../sandbox/projectRun';
import useDefaultApp from '../../../hooks/useDefaultApp';
import ServiceActions from '../../sandbox/ServiceActions';
import GadgetCard, { Quiet } from './GadgetCard';

/** The iframe's virtual viewport for the box `ref` points at, kept in step with the box's size while `active`. */
function usePreviewFit(ref, active) {
  const [fit, setFit] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) { return undefined; }
    const measure = () => {
      const next = previewFit({ width: el.clientWidth, height: el.clientHeight });
      setFit((prev) => (prev && next && prev.scale === next.scale && prev.width === next.width && prev.height === next.height ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, active]);
  return fit;
}

const STATE_TEXT = { building: 'Starting…', failed: 'Its build failed.', crashed: 'It crashed.', stopped: 'Not running.' };

export default function PreviewGadget({ projectId, items = [], canRun = false, onChanged = null, onOpenLogs = null, runAll = null }) {
  const { defaultApp } = useDefaultApp(projectId);
  const item = mainServiceItem(items, defaultApp);
  const url = item ? appUrlOf(projectId, item) : null;
  const frameRef = useRef(null);
  const fit = usePreviewFit(frameRef, Boolean(url));
  if (!item) {
    return <GadgetCard testId="gadget-preview-card" title="Preview"><Quiet>No service to preview yet.</Quiet></GadgetCard>;
  }
  // The default app may be a file pinned in a service (utils/defaultApp): named as Apps does, "app · docker-compose.yaml".
  const name = item.runnable ? `${shortServiceName(item.runnable.service)} · ${item.runnable.file}` : shortServiceName(item.serviceName);
  const state = serviceState(item.service);
  return (
    <GadgetCard
      links={url ? <button className="ph-link" data-testid="preview-open" onClick={() => openApp(projectId, item)} type="button">Open</button> : null}
      meta={name}
      testId="gadget-preview-card"
      title="Preview"
    >
      {url ? (
        <div className="gd-frame" data-testid="preview-frame" ref={frameRef}>
          <iframe
            aria-hidden="true"
            loading="lazy"
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-forms"
            src={url}
            style={fit ? { width: `${fit.width}px`, height: `${fit.height}px`, transform: `scale(${fit.scale})` } : undefined}
            tabIndex={-1}
            title={`Preview of ${name}`}
          />
        </div>
      ) : (
        <div className="gd-frame gd-frame-off" data-testid="preview-off">
          {STATE_TEXT[state] || 'Not running.'}{canRun && state !== 'building' ? ' Start it to see it here.' : ''}
        </div>
      )}
      {canRun && !url && runAll && <div className="gd-actions" data-testid="preview-actions">{runAll}</div>}
      {canRun && (url || !runAll) && (
        <div className="gd-actions" data-testid="preview-actions">
          <ServiceActions onChanged={onChanged} onOpenLogs={onOpenLogs} projectId={projectId} runnable={item.runnable || null} service={item.service} serviceName={item.serviceName} showRemove={false} />
        </div>
      )}
    </GadgetCard>
  );
}

PreviewGadget.propTypes = {
  // false for a viewer: no Start, Stop or Restart.
  canRun: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({ serviceName: PropTypes.string, service: PropTypes.object, runnable: PropTypes.object })),
  onChanged: PropTypes.func,
  onOpenLogs: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  // Not running: Start all of the project (RunAllActions) instead of the main service's own Start.
  runAll: PropTypes.node,
};
